// utils/logicChainConverter.js
// Converts the search interface logic chain into ORF payload format

export const convertLogicChainToORFPayload = (logicChain, segmentsData) => {
	if (!logicChain || logicChain.length === 0) {
		return null;
	}

	console.log("🔄 Converting logic chain to ORF payload:", logicChain);

	// Helper function to get segment details
	const getSegmentDetails = (segmentId) => {
		for (const [searchTerm, data] of Object.entries(segmentsData)) {
			if (data && data.data) {
				const segment = data.data.find(
					(s) => s.segmentVector?.segmentId === segmentId,
				);
				if (segment) {
					return {
						id: segmentId,
						reach: segment.segmentMetrics?.reach || 0,
					};
				}
			}
		}
		return { id: segmentId, reach: 0 };
	};

	// Helper function to get segments from search terms
	const getSegmentsFromTerm = (searchTerm) => {
		const segments = [];
		for (const [key, data] of Object.entries(segmentsData)) {
			if (
				data &&
				data.data &&
				key.toLowerCase().includes(searchTerm.toLowerCase())
			) {
				data.data.forEach((segment) => {
					const segmentId = segment.segmentVector?.segmentId;
					if (segmentId) {
						segments.push({
							id: segmentId,
							reach: segment.segmentMetrics?.reach || 0,
						});
					}
				});
			}
		}
		return segments;
	};

	// Parse logic chain and group by operators
	const parseLogicChain = () => {
		const groups = [];
		let currentGroup = [];
		let currentGroupType = "UNION"; // Default to OR logic

		logicChain.forEach((item, index) => {
			const term = item.term;
			const operator = item.operator;

			// Get segments for this term
			const termSegments = getSegmentsFromTerm(term.text);

			if (term.type === "include") {
				// Add segments to current group
				currentGroup.push(...termSegments);

				// If next operator is AND, finish current group and start new one
				if (operator === "AND") {
					groups.push({
						type: currentGroupType,
						segments: [...currentGroup],
					});
					currentGroup = [];
					currentGroupType = "UNION";
				}
				// If next operator is OR or no operator, continue building current group
				else if (operator === "OR" || !operator) {
					// Continue adding to current group
				}
			}
			// Handle NOT/exclude terms separately
			else if (term.type === "exclude") {
				// NOT terms will be handled after building the main expression
				// For now, we'll note them for later processing
			}
		});

		// Add the last group if it has segments
		if (currentGroup.length > 0) {
			groups.push({
				type: currentGroupType,
				segments: [...currentGroup],
			});
		}

		return groups;
	};

	const groups = parseLogicChain();
	console.log("📊 Parsed groups:", groups);

	// Build ORF expression
	const buildORFExpression = (groups) => {
		if (groups.length === 0) {
			return null;
		}

		// If only one group, return it directly
		if (groups.length === 1) {
			const group = groups[0];
			if (group.segments.length === 1) {
				return group.segments[0];
			} else {
				return {
					op: group.type,
					children: group.segments,
				};
			}
		}

		// Multiple groups - create INTERSECTION
		const children = groups.map((group) => {
			if (group.segments.length === 1) {
				return group.segments[0];
			} else {
				return {
					op: group.type,
					children: group.segments,
				};
			}
		});

		return {
			op: "INTERSECTION",
			children: children,
		};
	};

	const expression = buildORFExpression(groups);

	if (!expression) {
		return null;
	}

	const payload = {
		expr: expression,
	};

	console.log("✅ Generated ORF payload:", JSON.stringify(payload, null, 2));
	return payload;
};

// Alternative simpler converter for direct segment selections
export const convertSelectedSegmentsToORFPayload = (
	selectedSegments,
	selectedSets,
	segmentsData,
	cumulativeData,
) => {
	console.log("🔄 Converting selected segments to ORF payload");

	const segments = [];

	// Helper function to get segment details
	const getSegmentDetails = (segmentId) => {
		for (const [searchTerm, data] of Object.entries(segmentsData)) {
			if (data && data.data) {
				const segment = data.data.find(
					(s) => s.segmentVector?.segmentId === segmentId,
				);
				if (segment) {
					return {
						id: segmentId,
						reach: segment.segmentMetrics?.reach || 0,
					};
				}
			}
		}
		return { id: segmentId, reach: 0 };
	};

	// Add directly selected segments
	selectedSegments.forEach((segmentId) => {
		const details = getSegmentDetails(segmentId);
		segments.push(details);
	});

	// Add segments from selected sets
	selectedSets.forEach((setId) => {
		const cleanSetId = setId.includes("_") ? setId.split("_").pop() : setId;

		// Find the set in cumulativeData and get its segment IDs
		for (const [key, data] of Object.entries(cumulativeData)) {
			if (key === cleanSetId || setId.includes(key)) {
				if (data.segmentIds) {
					const ids = data.segmentIds.split(",").map((id) => id.trim());
					ids.forEach((id) => {
						const segmentDetails = getSegmentDetails(id);
						segments.push(segmentDetails);
					});
				}
				break;
			}
		}
	});

	if (segments.length === 0) {
		return null;
	}

	// For simple selection, use UNION (OR logic)
	const payload = {
		expr: {
			op: "UNION",
			children: segments,
		},
	};

	console.log(
		"✅ Generated ORF payload from selections:",
		JSON.stringify(payload, null, 2),
	);
	return payload;
};

// Enhanced ORF calculation function that accepts custom payload
export const calculateORFWithCustomPayload = async (
	customPayload,
	apiService,
) => {
	try {
		// Check authentication first
		if (!apiService.isAuthenticated()) {
			throw new Error("Authentication required. Please sign in to continue.");
		}

		// Ensure valid token before making the request
		const hasValidToken = await apiService.ensureValidToken();
		if (!hasValidToken) {
			throw new Error("Unable to authenticate. Please sign in again.");
		}

		console.log("ORF Custom Payload:", JSON.stringify(customPayload, null, 2));

		// Use ApiService's fetchWithFallback
		const response = await apiService.fetchWithFallback(
			"/segment/calculate-orf",
			{
				method: "POST",
				headers: {
					accept: "application/json",
					"Content-Type": "application/json",
				},
				body: JSON.stringify(customPayload),
			},
		);

		if (!response.ok) {
			// Handle specific error cases
			if (response.status === 401 || response.status === 403) {
				throw new Error("Authentication failed. Please sign in again.");
			}
			throw new Error(`API Error: ${response.status} - ${response.statusText}`);
		}

		const data = await response.json();
		console.log("ORF Response:", data);
		return data;
	} catch (error) {
		console.error("Error calculating ORF with custom payload:", error);

		// Handle authentication errors specifically
		if (
			error.message.includes("Authentication") ||
			error.message.includes("sign in")
		) {
			// Trigger auth failure handling in ApiService
			apiService.handleAuthFailure(error);
		}

		throw error;
	}
};

// Utility function to validate ORF payload structure
export const validateORFPayload = (payload) => {
	if (!payload || !payload.expr) {
		return { valid: false, error: "Missing expr property" };
	}

	const validateNode = (node) => {
		// Terminal node (segment)
		if (node.id && typeof node.reach === "number") {
			return { valid: true };
		}

		// Operation node
		if (node.op && node.children && Array.isArray(node.children)) {
			if (!["UNION", "INTERSECTION"].includes(node.op)) {
				return { valid: false, error: `Invalid operation: ${node.op}` };
			}

			if (node.children.length === 0) {
				return { valid: false, error: "Operation node must have children" };
			}

			// Validate all children
			for (const child of node.children) {
				const childValidation = validateNode(child);
				if (!childValidation.valid) {
					return childValidation;
				}
			}

			return { valid: true };
		}

		return { valid: false, error: "Invalid node structure" };
	};

	return validateNode(payload.expr);
};
